import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { login, logout, setToken } from '@/store/slices/authSlice'
// import { setUser, clearUser, updateUserRoles, setPermissions } from '@/store/slices/userSlice'
import { clearUser } from '@/store/slices/userSlice'
import { isInvalidRefreshTokenError, tokenTracker, getTokenErrorMessage } from '@/services/token-refresh'

const AUTH_STORAGE_KEY = 'myassets_auth_state'

interface StoredAuthState {
  email: string | null
  roles: string[]
  permissions: string[]
  user: {
    id: string
    name: string
    email: string
    picture: string
  }
}

/**
 * Saves auth state to localStorage for persistence across page reloads
 */
const saveAuthState = (state: Partial<StoredAuthState>) => {
  try {
    const existing = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || '{}')
    const updated = { ...existing, ...state }
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(updated))
  } catch (e) {
    console.warn('Failed to save auth state to localStorage', e)
  }
}

/**
 * Restores auth state from localStorage with safe defaults
 */
const getStoredAuthState = (): StoredAuthState | null => {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY)
    if (!stored) return null
    
    const parsed = JSON.parse(stored)
    // Ensure arrays exist with defaults
    return {
      email: parsed.email || null,
      roles: Array.isArray(parsed.roles) ? parsed.roles : [],
      permissions: Array.isArray(parsed.permissions) ? parsed.permissions : [],
      user: parsed.user || { id: '', name: '', email: '', picture: '' }
    }
  } catch (e) {
    console.warn('Failed to restore auth state from localStorage', e)
    return null
  }
}

/**
 * Clears auth state from localStorage
 */
const clearStoredAuthState = () => {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY)
  } catch (e) {
    console.warn('Failed to clear auth state from localStorage', e)
  }
}

/**
 * Custom hook to sync Auth0 state with Redux state and persist to localStorage
 * Automatically handles login/logout and user profile updates
 * Restores auth state on mount for faster initial load
 */
export const useAuth0Redux = () => {
  const { isLoading, isAuthenticated, user, getAccessTokenSilently } = useAuth0()
  const dispatch = useAppDispatch()
  const hasRestoredRef = useRef(false)

  // Restore stored auth state on mount (before Auth0 finishes loading)
  useEffect(() => {
    if (!hasRestoredRef.current && isLoading) {
      const stored = getStoredAuthState()
      if (stored && stored.email) {
        dispatch(login(stored.email))
        // dispatch(setUser(stored.user))
        // Note: Token is NOT restored from localStorage for security reasons
        // Auth0 SDK will handle token management in memory
        // if (stored.roles && stored.roles.length) dispatch(updateUserRoles(stored.roles))
        // if (stored.permissions && stored.permissions.length) dispatch(setPermissions(stored.permissions))
        hasRestoredRef.current = true
      }
    }
  }, [isLoading, dispatch])

  // Sync Auth0 authentication state with Redux and persist
  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated && user) {
        // Check if we've already detected an invalid token - skip retry
        if (tokenTracker.isTokenInvalid()) {
          console.warn('Invalid refresh token previously detected - skipping token fetch');
          dispatch(logout());
          dispatch(clearUser());
          clearStoredAuthState();
          return;
        }

        const email = user.email || ''

        // User is authenticated - sync to Redux
        dispatch(login(email))

        // Basic user sync
        const userData = {
          id: user.sub || '',
          name: user.name || '',
          email,
          picture: (user as any).picture || '',
        }
        // dispatch(setUser(userData))

        // Get access token and store it in Redux for API calls (but NOT in localStorage for security)
        getAccessTokenSilently()
          .then((token) => {
            // Clear any previous invalid token flag on success
            tokenTracker.clearInvalidTokenFlag();

            // Store token in Redux for current session (in-memory, not persisted)
            dispatch(setToken(token))

            // Save user metadata to localStorage for persistence (EXCLUDING token for security)
            saveAuthState({
              email,
              user: userData,
            })

            try {
              const tokenParts = token.split('.')
              if (tokenParts.length !== 3) {
                console.warn('Invalid token format: expected JWT with 3 parts')
                return
              }

              const base64Url = tokenParts[1]
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
              const jsonPayload = decodeURIComponent(
                atob(base64)
                  .split('')
                  .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                  })
                  .join('')
              )
              const payload = JSON.parse(jsonPayload)

              // Common claim names for roles/permissions
              let roles: string[] = []
              let permissions: string[] = []

              if (Array.isArray(payload.roles)) roles = payload.roles
              if (Array.isArray(payload.permissions)) permissions = payload.permissions

              // Check for namespaced claims (e.g., https://yourapp.com/roles)
              Object.keys(payload).forEach((k) => {
                if (!roles.length && /roles$/i.test(k) && Array.isArray(payload[k])) {
                  roles = payload[k]
                }
                if (!permissions.length && /permissions$/i.test(k) && Array.isArray(payload[k])) {
                  permissions = payload[k]
                }
              })

              if (roles.length) {
                // dispatch(updateUserRoles(roles))
                // Update localStorage with roles
                saveAuthState({ roles })
              }
              if (permissions.length) {
                // dispatch(setPermissions(permissions))
                // Update localStorage with permissions
                saveAuthState({ permissions })
              }
            } catch (e) {
              console.error('Failed to parse access token for roles/permissions', e)
            }
          })
          .catch((err) => {
            // Detect if this is an invalid refresh token error
            const isInvalid = isInvalidRefreshTokenError(err);
            const errorMsg = getTokenErrorMessage(err);

            if (isInvalid) {
              console.error('Invalid or expired refresh token detected - marking for auto-logout', err);
              // Mark token as invalid to prevent retry loops
              tokenTracker.markTokenAsInvalid();
            } else {
              console.error('Error getting token:', err);
            }

            // Auto-logout without retry
            dispatch(logout());
            dispatch(clearUser());
            clearStoredAuthState();

            // Log the human-readable error message
            console.warn('Authentication error:', errorMsg);
          })
      } else if (!isAuthenticated) {
        // User is not authenticated - clear Redux state and localStorage
        dispatch(logout())
        dispatch(clearUser())
        clearStoredAuthState()
        // Clear the invalid token flag when user logs out
        tokenTracker.clearInvalidTokenFlag();
      }
    }
  }, [isLoading, isAuthenticated, user, dispatch, getAccessTokenSilently])

  return { isLoading, isAuthenticated, user }
}

